<template>
  <BgImageLayout title="Stakeholders" :page="__guide.stakeholdersPage" icon-color="white">
    <AgGridWrapper
      ref="wrapper"
      :actions="[{ label: 'Create new Stakeholder', event: 'show-form', size: 'md' }]"
      :options="gridOptions"
      :separator="true"
      :include-search-box="true"
      handle-update-on-parent
      store-namespace="stakeholders"
      service-namespace="stakeholders"
      @show-form="showFormModal"
      @cell-updated="cellUpdated"
    />

    <TransferStakesModal ref="$transferStakesModal" :stakeholders="stakeholders" />

    <form-create-stakeholder
      :showing="showModal"
      :departments="departments"
      @refresh="stakeholderAdded"
      @close="closeFormModal"
    />

    <ConfirmationDialog
      v-if="deletable"
      :uid="uid"
      :entity="deletable"
      :replacement-options="stakeholderOptions"
      store-namespace="stakeholders"
      @done="stakeDeleted"
    />
  </BgImageLayout>
</template>

<script>
import DepartmentsModule from './departments'
import { mapGetters, mapMutations } from 'vuex'
import { dateRenderer, dateSorter } from '@/lib/generic.utils'
import LmEntityActions from '@components/ag-grid-helpers/EntityActions'
import { get, isEqual, cloneDeep } from 'lodash'
import { uid } from 'quasar'
import axios from 'axios'
import * as apiTo from '@api/api-routes'
import { LmFormObject } from '@lib/models/lm-form-object.class'
// Components
import { FormCreateStakeholder } from '@components/forms'
import AgGridWrapper from '@components/ag-grid-helpers/AgGridWrapper'
import ConfirmationDialog from '@components/lm-blocks/Dialog'
import AgGridSelect from '@components/ag-grid-helpers/AgGridSelect.vue'
import TransferStakesModal from './TransferStakesModal.vue'

export default {
  name: 'LmPageStakeholderIndex',

  components: {
    FormCreateStakeholder,
    AgGridWrapper,
    ConfirmationDialog,
    TransferStakesModal
  },

  data() {
    return {
      showModal: false,
      deletable: null,
      uid: uid(),
      stakeholders: [],
      stakeholder: new LmFormObject({
        first_name: null,
        last_name: null,
        email: null,
        phone: null,
        job: null,
        department: null
      })
    }
  },

  computed: {
    ...mapGetters('departments', ['departments']),

    stakeholderOptions() {
      return this.generateOptions({
        collection: this.stakeholders,
        excludeValue: get(this.deletable, 'id'),
        labelKey: 'first_name',
        combineLabelWith: 'last_name',
        concatSep: ' '
      })
    },

    gridOptions() {
      return {
        rowData: cloneDeep(this.stakeholders),
        frameworkComponents: {
          'ag-grid-select': AgGridSelect
        },
        context: {
          actions: [
            {
              icon: 'trash',
              tooltip: 'Delete',
              onClick: this.delete,
              color: 'faded'
            },
            {
              icon: 'fa fa-turn-down fa-rotate-270',
              tooltip: 'Transfer stakes',
              onClick: this.transfer,
              color: 'faded'
            }
          ]
        },
        columnDefs: [
          {
            headerName: 'First Name',
            field: 'first_name',
            editable: true,
            cellRenderer: params => (params && params.value ? params.value : 'Insert'),
            tooltipValueGetter: params => (params && params.value ? params.value : 'Insert')
          },
          {
            headerName: 'Last Name',
            field: 'last_name',
            editable: true,
            cellRenderer: params => (params && params.value ? params.value : 'Insert'),
            tooltipValueGetter: params => (params && params.value ? params.value : 'Insert')
          },
          {
            headerName: 'Email',
            field: 'email',
            editable: true,
            cellRenderer: params => (params && params.value ? params.value : 'Insert'),
            tooltipValueGetter: params => (params && params.value ? params.value : 'Insert')
          },
          {
            headerName: 'Phone',
            field: 'phone',
            editable: true,
            cellRenderer: params => (params && params.value ? params.value : 'Insert'),
            tooltipValueGetter: params => (params && params.value ? params.value : 'Insert')
          },
          {
            headerName: 'Job',
            field: 'job',
            editable: true,
            cellRenderer: params => (params && params.value ? params.value : 'Insert'),
            tooltipValueGetter: params => (params && params.value ? params.value : 'Insert')
          },
          {
            headerName: 'Departments',
            field: 'departments',
            editable: true,
            cellEditorParams: params => {
              return {
                options: 'departments/departments',
                multiselect: true,
                optionValue: 'id',
                optionLabel: 'name',
                returnObject: true,
                selected: params.data.departments.map(d => d.id)
              }
            },
            cellEditor: 'ag-grid-select',
            cellRenderer: params => {
              return params.data.departments.map(item => item.name).join() || 'Select'
            },
            maxWidth: 250
          },
          {
            headerName: 'Created',
            field: 'created_at',
            comparator: dateSorter,
            cellRenderer: params => dateRenderer(params.value),
            tooltipValueGetter: params => dateRenderer(params.value)
          },
          {
            headerName: 'Updated',
            field: 'updated_at',
            comparator: dateSorter,
            cellRenderer: params => dateRenderer(params.value),
            tooltipValueGetter: params => dateRenderer(params.value)
          },
          {
            headerName: 'Actions',
            cellRendererFramework: LmEntityActions
          }
        ]
      }
    }
  },

  created() {
    this.$store.registerModule('departments', DepartmentsModule)
    axios.all([apiTo.getStakesDepartments(), apiTo.departmentsApi('get')]).then(
      axios.spread(({ data: stakes }, { data: deps }) => {
        this.stakeholders = stakes.result
        this.setDepartments(deps.result)
      })
    )
  },

  beforeDestroy() {
    this.$store.commit('departments/setDepartments', [])
    this.$store.unregisterModule('departments')
  },

  methods: {
    ...mapMutations('departments', ['setDepartments']),

    showFormModal() {
      this.showModal = true
    },

    closeFormModal() {
      this.showModal = false
    },

    stakeholderAdded(newStake) {
      this.closeFormModal()
      this.stakeholders.push(newStake)
    },

    delete(params) {
      this.$set(this, 'deletable', params.data)
      this.$store.dispatch('stakeholders/deleteConfirmation', {
        id: this.deletable.id,
        dialogUid: this.uid
      })
    },

    transfer(params) {
      this.$refs.$transferStakesModal.show(params.data)
    },

    stakeDeleted() {
      const index = this.stakeholders.findIndex(stake => stake.id === this.deletable.id)
      if (~index) this.stakeholders.splice(index, 1)
    },

    cellUpdated({ newValue, oldValue, data }) {
      const newVal = cloneDeep(newValue) || null
      const oldVal = cloneDeep(oldValue) || null

      if (isEqual(newVal, oldVal)) return

      const { id, ...payload } = data
      payload.departments = payload.departments.map(dep => dep.id)
      apiTo.updateStakeholder(id, payload).then(({ data }) => this.$refs.wrapper.updateRows([data.result]))
    }
  }
}
</script>
